import React, { useEffect, useState } from "react";
import ClipLoader from "react-spinners/ClipLoader";
import "./ScholarLeague.scss";
import axios from "axios";
import { url_287, url_292, url_293 } from "../../../../custom_utilities/api_services";
import useAuthUtils from "../../../../hooks/auth/useAuthUtils";
import LeagueAchievements from "./LeagueAchievements";
import { findTierIndexForXp } from "./leagueXpRange";
import ScholarLeagueBadgesAndEarnPoints from "./ScholarLeagueBadgesAndEarnPoints";

const ScholarLeague = ({ showHeader = true, compact = false }) => {
  const { headers } = useAuthUtils();
  const authorization = headers?.Authorization;
  const [leagueData, setLeagueData] = useState(null);
  const [leagueSteps, setLeagueSteps] = useState([]);
  const [earnPoints, setEarnPoints] = useState([]);
  const [leaderboardData, setLeaderboardData] = useState(null);
  const [overallGoalProgress, setOverallGoalProgress] = useState(null);
  const [isLoading, setIsLoading] = useState(() => Boolean(authorization));

  useEffect(() => {
    let cancelled = false;

    const fetchBadgeTiers = async () => {
      try {
        const response = await axios.get(url_287, {
          headers: { Authorization: authorization },
        });
        if (response?.data?.status !== 200 || !response?.data?.data) {
          return;
        }

        const payload = response.data.data;
        const badges = Array.isArray(payload?.badges) ? payload.badges : [];
        const pointItems = Array.isArray(payload?.earn_points) ? payload.earn_points : [];

        const tierRingAccentByName = (rawName) => {
          const k = String(rawName || "").toLowerCase().trim();
          const map = {
            beginner: "#64748b",
            aspirant: "#ca8a04",
            achiever: "#22c55e",
            scholar: "#4f46e5",
            champion: "#ea580c",
            ranker: "#0d9488",
          };
          return map[k] || "#475569";
        };

        const formatBadgeRangeLabel = (badge) => {
          const raw = String(badge?.xp_ranges ?? "").trim();
          if (raw) return `${raw} pts`;
          const minNum = Number(badge?.min_xp);
          const maxNum = Number(badge?.max_xp);
          if (Number.isFinite(minNum) && Number.isFinite(maxNum)) {
            const lo = Math.min(minNum, maxNum);
            const hi = Math.max(minNum, maxNum);
            return `${lo}-${hi} pts`;
          }
          return "";
        };

        const badgesMatchApiCurrent = (badge, apiCurrent) => {
          if (!apiCurrent || !badge) return false;
          if (
            apiCurrent?.id != null &&
            badge?.id != null &&
            Number(apiCurrent.id) === Number(badge.id)
          ) {
            return true;
          }
          const a = String(apiCurrent?.badge_name || "").trim().toLowerCase();
          const b = String(badge?.badge_name || "").trim().toLowerCase();
          return Boolean(a && b && a === b);
        };

        const hereLabelFromApi = (badge, badgeStatus, apiCurrent) => {
          if (badgeStatus !== "current") return "";
          const fromRow = String(badge?.position ?? "").trim();
          if (fromRow) return fromRow;
          if (badgesMatchApiCurrent(badge, apiCurrent)) {
            return String(apiCurrent?.position ?? "").trim();
          }
          return "";
        };

        const apiCurrentBadge = payload?.current_badge;

        let currentTierIndex = -1;
        const xpParsed = Number(payload?.weekly_xp);
        if (Number.isFinite(xpParsed)) {
          currentTierIndex = findTierIndexForXp(badges, xpParsed);
        }

        if (currentTierIndex < 0 && apiCurrentBadge) {
          currentTierIndex = badges.findIndex((b) => badgesMatchApiCurrent(b, apiCurrentBadge));
        }

        if (currentTierIndex < 0) {
          currentTierIndex = badges.findIndex((b) => b?.is_current);
        }

        const normalizedTiers = badges.map((badge, index) => {
          const name = badge?.badge_name || "";
          const achievedCount = Number(badge?.achieved_count || 0);
          const isAchievedFromApi =
            achievedCount > 0 || String(badge?.status || "").trim().toLowerCase() === "achieved";
          const isCurrent = !!badge?.is_current;
          let status = "locked";

          // Resolve "current" from tier order / XP / current_badge first. If we treated
          // achieved_count as primary, the live tier (e.g. Achiever) could stay "achieved"
          // and never get the timeline "You are here" UI.
          if (currentTierIndex >= 0) {
            if (index === currentTierIndex) status = "current";
            else if (index < currentTierIndex) status = "achieved";
            else status = "locked";
          } else if (isCurrent) {
            status = "current";
          } else if (isAchievedFromApi) {
            status = "achieved";
          }

          return {
            id: badge?.id,
            name,
            range: formatBadgeRangeLabel(badge),
            status,
            iconUrl: badge?.badge_icon_url || "",
            achievedCount,
            ringAccent: tierRingAccentByName(name),
            hereLabel: hereLabelFromApi(badge, status, apiCurrentBadge),
          };
        });

        setLeagueData(payload);
        setLeagueSteps(normalizedTiers);
        setEarnPoints(pointItems);
      } catch (error) {
        setLeagueData(null);
        setLeagueSteps([]);
        setEarnPoints([]);
      }
    };

    const fetchLeaderboard = async () => {
      try {
        const response = await axios.get(url_292, {
          headers: { Authorization: authorization },
        });
        const payload = response?.data?.data;
        if (response?.data?.status !== 200 || !payload || typeof payload !== "object") {
          setLeaderboardData(null);
          return;
        }
        setLeaderboardData(payload);
      } catch (error) {
        setLeaderboardData(null);
      }
    };

    const fetchOverallGoalProgress = async () => {
      try {
        const response = await axios.get(url_293, {
          headers: { Authorization: authorization },
        });
        if (response?.data?.status !== 200) {
          setOverallGoalProgress(null);
          return;
        }
        setOverallGoalProgress(response?.data?.data ?? null);
      } catch (error) {
        setOverallGoalProgress(null);
      }
    };

    if (!authorization) {
      setLeagueData(null);
      setLeagueSteps([]);
      setEarnPoints([]);
      setLeaderboardData(null);
      setOverallGoalProgress(null);
      setIsLoading(false);
      return () => {
        cancelled = true;
      };
    }

    setIsLoading(true);

    (async () => {
      await Promise.allSettled([
        fetchBadgeTiers(),
        fetchLeaderboard(),
        fetchOverallGoalProgress(),
      ]);
      if (!cancelled) setIsLoading(false);
    })();

    return () => {
      cancelled = true;
    };
  }, [authorization]);

  if (isLoading) {
    return (
      <section
        className="scholar-league-page scholar-league-page--loading"
        role="status"
        aria-live="polite"
        aria-busy="true"
        aria-label="Loading league"
      >
        <ClipLoader color="#C61C26" size={48} />
      </section>
    );
  }

  return (
    <section className="scholar-league-page">
      <LeagueAchievements
        leagueData={leagueData}
        leagueSteps={leagueSteps}
        showHeader={showHeader}
        compact={compact}
        leaderboardData={leaderboardData}
        overallGoalProgress={overallGoalProgress}
      />
      <ScholarLeagueBadgesAndEarnPoints
        leagueData={leagueData}
        leagueSteps={leagueSteps}
        earnPoints={earnPoints}
      />
    </section>
  );
};

export default ScholarLeague;
